import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { BrowserInfo, detect } from 'detect-browser';
import { omit } from 'lodash';
import {
  IButtonEvent,
  ICalculatorEvent,
  ICheckboxEvent,
  ILinkEvent,
  ISelectEvent,
  ITextInputEvent,
} from './events/interfaces';
import Queue from './Queue';
import EventsHistory from './EventsHistory';
import fetchEventCollector from './fetchEventCollector';
import {
  EventCollectorPayload,
  IdentifyEventPayload,
  PageEventPayload,
  TrackEventPayload,
} from './payloads';
import TextInputEvent from './events/TextInputEvent';
import { IPageEvent } from './events/PageEvent';
import { IIdentifyEvent } from './events/IdentifyEvent';
import { IFileSelectEvent } from './events/FileSelectEvent';

export type IEventType =
  | IButtonEvent
  | ICheckboxEvent
  | ITextInputEvent
  | ICalculatorEvent
  | ISelectEvent
  | ILinkEvent
  | IFileSelectEvent;

export const queue = new Queue<EventCollectorPayload>();
export const eventsHistory = new EventsHistory<EventCollectorPayload>();
const browser = detect();

function initLoginId() {
  const loginIdSessionStorage = sessionStorage.getItem('loginId');
  if (loginIdSessionStorage) {
    return loginIdSessionStorage;
  }
  const loginId = uuid();
  sessionStorage.setItem('loginId', loginId);
  return loginId;
}

function initPreviousPageTs() {
  const previousPageTsStr = sessionStorage.getItem('previousPageTs');
  if (previousPageTsStr) {
    const parsed = parseInt(previousPageTsStr, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
}

function getShouldSendEvents() {
  if (import.meta.env.VITE_FEATURE_EVENT_COLLECTOR_SWITCHER === 'true') {
    return localStorage.getItem('eventCollectorShouldSendEvents') === 'true';
  }

  return true;
}

export default function useEventCollectorValue() {
  const uidRef = useRef<string | null>(null);
  const applicationIdRef = useRef<string | null>(null);
  const loginIdRef = useRef<string>(uuid());
  const previousPageTsRef = useRef<number | null>(null);
  const [isSendingEvents, setIsSendingEvents] = useState(getShouldSendEvents());

  const handleToggleSendEvents = useCallback(() => {
    setIsSendingEvents((prev) => {
      localStorage.setItem('eventCollectorShouldSendEvents', `${!prev}`);
      return !prev;
    });
  }, []);

  const track = useCallback(async (e: IEventType) => {
    const { type, location, timestamp, ...rest } = e.toJSON();

    if (e instanceof TextInputEvent && e.isEmail) {
      const hash = await e.getHash();
      uidRef.current = hash;
      sessionStorage.setItem('uid', hash);
    }

    const trackEventPayload: TrackEventPayload = {
      applicationId: applicationIdRef.current,
      uid: uidRef.current,
      loginId: loginIdRef.current,
      ts: timestamp,
      location,
      type,
      target: rest,
    };

    queue.enqueue(trackEventPayload);
  }, []);

  const page = useCallback((e: IPageEvent) => {
    const timeSpent =
      previousPageTsRef.current !== null
        ? e.timestamp - previousPageTsRef.current
        : null;
    previousPageTsRef.current = e.timestamp;
    sessionStorage.setItem(
      'previousPageTs',
      previousPageTsRef.current.toString(10),
    );

    const pageEvent: PageEventPayload = {
      applicationId: applicationIdRef.current,
      uid: uidRef.current,
      loginId: loginIdRef.current,
      type: e.type,
      properties: {
        ...omit(e.toJSON(), ['type', 'timestamp']),
        prevPathTimeSpent: timeSpent,
      },
      ts: e.timestamp,
      app: {
        applicationType: 'new',
        env: import.meta.env.VITE_ENV,
        type: 'web',
        version: '1.0.0',
      },
      deviceInfo: { browser: browser as BrowserInfo },
    };
    queue.enqueue(pageEvent);
  }, []);

  const identify = useCallback(async (e: IIdentifyEvent) => {
    if (e.application?.id) {
      applicationIdRef.current = e.application.id;
      sessionStorage.setItem('applicationId', e.application.id);
    }

    if (e.hasEmail) {
      const hash = await e.getHash();
      uidRef.current = hash;
      sessionStorage.setItem('uid', hash);
    }

    const identifyEvent: IdentifyEventPayload = {
      applicationId: applicationIdRef.current,
      uid: uidRef.current,
      loginId: loginIdRef.current,
      type: e.type,
      ts: e.timestamp,
      traits: {
        client: e.client,
        application: e.application,
        email: e.email,
        phone: e.phone,
      },
    };
    queue.enqueue(identifyEvent);
  }, []);

  const initialize = useCallback(() => {
    loginIdRef.current = initLoginId();
    previousPageTsRef.current = initPreviousPageTs();
    applicationIdRef.current = sessionStorage.getItem('applicationId');
    uidRef.current = sessionStorage.getItem('uid');
  }, []);

  const reset = useCallback(() => {
    previousPageTsRef.current = null;
    sessionStorage.removeItem('previousPageTs');

    loginIdRef.current = uuid();
    sessionStorage.setItem('loginId', loginIdRef.current);

    uidRef.current = null;
    sessionStorage.removeItem('uid');

    applicationIdRef.current = null;
    sessionStorage.removeItem('applicationId');

    queue.clear();
    eventsHistory.clear();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (queue.isEmpty()) {
        return;
      }

      const event = queue.dequeue();

      if (!event) {
        return;
      }

      if (
        import.meta.env.VITE_FEATURE_EVENT_COLLECTOR === 'true' &&
        isSendingEvents
      ) {
        fetchEventCollector(event);
      }

      if (import.meta.env.VITE_FEATURE_EVENT_COLLECTOR_DEVTOOL === 'true') {
        eventsHistory.add(event);
      }
    }, 500);

    // Cleanup function
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    initialize();
  }, []);

  return {
    initialize,
    reset,
    track,
    page,
    identify,
    handleToggleSendEvents,
    isSendingEvents,
  };
}
